import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    XCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy,
    Info
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import customerService from '../../../api/crm/customers/customerService';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';

const MAX_UPLOAD_ROWS = 2000;

/**
 * BulkCustomerUpload - Modal for bulk uploading customers from an Excel file
 */
const BulkCustomerUpload = ({ isOpen, onComplete, onCancel }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedCustomers, setParsedCustomers] = useState([]);
    const [previewData, setPreviewData] = useState(null);   // validated rows from backend
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [progress, setProgress] = useState(null);   // { current, total, percent, created, failed }
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);

    // Reset and Close
    const resetState = useCallback(() => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
        setStep('upload');
        setFile(null);
        setParsedCustomers([]);
        setPreviewData(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
        setProgress(null);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onCancel();
    }, [resetState, onCancel]);

    // Stop polling if the component unmounts mid-import.
    useEffect(() => () => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    // File Selection & Parsing (Excel only)
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        const isExcel = /\.(xlsx|xls)$/i.test(selectedFile.name);
        if (!isExcel) {
            toast.error('Please select an Excel file (.xlsx or .xls)');
            return;
        }

        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        const reader = new FileReader();
        reader.onload = async (ev) => {
            try {
                const data = new Uint8Array(ev.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

                if (!rows || rows.length === 0) {
                    toast.error('No valid customer data found in the Excel file');
                    setFile(null);
                    return;
                }

                if (rows.length > MAX_UPLOAD_ROWS) {
                    toast.error(`The file has ${rows.length} rows, which exceeds the maximum of ${MAX_UPLOAD_ROWS} per upload. Please split the file.`);
                    setFile(null);
                    return;
                }

                // Normalizing keys and values
                const normalized = rows.map(row => {
                    const clean = {};
                    Object.keys(row).forEach(k => {
                        const val = row[k];
                        clean[String(k).trim()] = String(val == null ? '' : val).trim();
                    });
                    return clean;
                });

                setParsedCustomers(normalized);
                setStep('preview');
                await validateCustomers(normalized);
            } catch (err) {
                toast.error('Failed to read the Excel file. Please use the provided template.');
                setFile(null);
            }
        };
        reader.onerror = () => {
            toast.error('Failed to read the file.');
            setFile(null);
        };
        reader.readAsArrayBuffer(selectedFile);
    };

    // Backend Validation
    const validateCustomers = async (customers) => {
        setIsValidating(true);
        try {
            const response = await customerService.bulkPreview(customers);
            if (response.success) {
                setPreviewData(response.data);
            } else {
                toast.error(response.error || 'Server validation failed');
            }
        } catch (err) {
            toast.error('Validation failed: ' + err.message);
        } finally {
            setIsValidating(false);
        }
    };

    // Poll import progress until the batch reaches a terminal state.
    const pollProgress = useCallback((batchId) => {
        const tick = async () => {
            const res = await customerService.getBulkUploadProgress(batchId);

            if (!res.success) {
                setUploadResult({ success: false, error: res.error });
                setStep('result');
                setIsUploading(false);
                return;
            }

            const p = res.data || {};
            setProgress({
                current: p.current || 0,
                total: p.total || 0,
                percent: p.percent || 0,
                created: p.created || 0,
                updated: p.updated || 0,
                failed: p.failed || 0
            });

            const terminal = ['COMPLETED', 'PARTIAL_SUCCESS', 'FAILED'];
            if (terminal.includes(p.status)) {
                const result = p.result || {};
                if (p.status === 'FAILED' && !p.result) {
                    setUploadResult({ success: false, error: p.error || 'Upload failed' });
                    toast.error(p.error || 'Upload failed');
                } else {
                    setUploadResult({ success: p.status !== 'FAILED', data: result });
                    const created = result.created || 0;
                    const updated = result.updated || 0;
                    const parts = [];
                    if (created > 0) parts.push(`${created} created`);
                    if (updated > 0) parts.push(`${updated} updated`);
                    toast.success(parts.length ? `Successfully ${parts.join(', ')}` : 'Bulk upload completed');
                    if (result.failed > 0) {
                        toast.error(`${result.failed} rows failed to save.`);
                    }
                }
                setStep('result');
                setIsUploading(false);
                return;
            }

            pollRef.current = setTimeout(tick, 1000);
        };

        tick();
    }, []);

    // Final Upload Logic — starts async import, then polls for progress.
    const handleUpload = async () => {
        const validCustomers = previewData?.valid?.map(r => r.data);
        if (!validCustomers?.length) return;

        setIsUploading(true);
        setProgress({ current: 0, total: validCustomers.length, percent: 0, created: 0, updated: 0, failed: 0 });
        setStep('uploading');
        try {
            const response = await customerService.bulkUpload(validCustomers, file?.name || '');
            if (response.success && response.data?.batchId) {
                pollProgress(response.data.batchId);
            } else {
                setUploadResult({ success: false, error: response.error || 'Upload failed' });
                setStep('result');
                setIsUploading(false);
                toast.error(response.error || 'Upload failed');
            }
        } catch (err) {
            setUploadResult({ success: false, error: err.message });
            setStep('result');
            setIsUploading(false);
            toast.error(err.message);
        }
    };

    // Template Support
    const handleDownloadTemplate = () => {
        const headers = [
            'customerId',
            'companyName', 'contactPerson', 'email', 'phone', 'website',
            'businessType', 'industry', 'companySize', 'annualRevenue',
            'gstNumber', 'pan', 'drugLicenseNumber', 'region', 'decisionMaker',
            'billingAddress_street', 'billingAddress_city', 'billingAddress_state', 'billingAddress_pincode',
            'notes',
            'assignee', 'review', 'reviewer'
        ];
        const samples = [
            ['', 'Acme Corp', 'John Doe', 'john@acme.com', '9876543210', 'https://acme.com', 'retailer', 'Software', '10-50', '1000000', '27AAAAA0000A1Z5', 'ABCDE1234F', 'DL12345', 'North', 'Jane Smith', '123 Main St', 'Mumbai', 'Maharashtra', '400001', 'Priority customer', 'EMP001', 'Strong onboarding, high potential', 'manager@company.com'],
            ['CUST-2026-3894', 'Global Industries', 'Alice Wong', 'alice@global.com', '9123456789', 'https://global.com', 'distributor', 'Manufacturing', '500+', '50000000', '', '', '', 'West', 'Bob Lee', '456 Tech Park', 'Bangalore', 'Karnataka', '560001', 'Update existing customer', 'sales2@company.com', 'Updated via bulk upload', '']
        ];

        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...samples]);
        worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
        XLSX.writeFile(workbook, 'customer_bulk_upload_template.xlsx');
        toast.success('Template downloaded');
    };

    // counts
    const validCount = previewData?.summary?.valid || 0;
    const errorCount = previewData?.summary?.invalid || 0;
    const createCount = previewData?.summary?.toCreate || 0;
    const updateCount = previewData?.summary?.toUpdate || 0;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 z-[100]" onClick={handleClose} />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col z-[101]">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-50 rounded-lg">
                            <Upload className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Customer Upload</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload an Excel file to create or update customers'}
                                {step === 'preview' && (isValidating ? 'Validating file data...' : `${validCount} of ${parsedCustomers.length} rows valid (${createCount} create, ${updateCount} update)`)}
                                {step === 'uploading' && 'Processing customers...'}
                                {step === 'result' && 'Process finished'}
                            </p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="h-5 w-5 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* STEP 1: UPLOAD */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-green-500 hover:bg-green-50/20 transition-all cursor-pointer"
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const drFile = e.dataTransfer.files[0];
                                    if (drFile) handleFileChange({ target: { files: [drFile] } });
                                }}
                            >
                                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700">Drop your Excel file here</p>
                                <p className="text-sm text-gray-500 mt-1">or click to browse (.xlsx / .xls, Max 5MB)</p>
                                <div className="mt-5">
                                    <Button variant="outline" icon={<Upload className="h-4 w-4" />}>Select Excel File</Button>
                                </div>
                            </div>

                            <div className="bg-green-50 border border-green-100 rounded-xl p-5 flex items-start space-x-4">
                                <FileText className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                                <div>
                                    <h4 className="text-sm font-semibold text-green-900 mb-1">Use the Standard Template</h4>
                                    <p className="text-xs text-green-700 mb-3">Ensure your Excel column headers match exactly to the system fields for better mapping.</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownloadTemplate}
                                        icon={<Download className="h-3 w-3" />}
                                        className="bg-white border-green-200 text-green-700 hover:bg-green-50"
                                    >Download Template</Button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <h5 className="text-xs font-bold text-gray-500 uppercase mb-2">Required Fields</h5>
                                    <ul className="text-xs text-gray-700 space-y-1">
                                        <li><strong>customerId:</strong> Leave blank to create. Provide e.g. CUST-2026-3894 to update.</li>
                                        <li><strong>companyName:</strong> Full business name</li>
                                        <li><strong>contactPerson:</strong> Required for new customers</li>
                                        <li><strong>email / phone:</strong> Required for new customers (at least one)</li>
                                    </ul>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <h5 className="text-xs font-bold text-gray-500 uppercase mb-2">Optional Fields</h5>
                                    <ul className="text-xs text-gray-700 space-y-1">
                                        <li><strong>businessType:</strong> retailer, distributor, hospital, etc.</li>
                                        <li><strong>gstNumber:</strong> GSTIN of the company</li>
                                        <li><strong>billingAddress_city:</strong> Service city</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-start space-x-4">
                                <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-blue-800 space-y-1">
                                    <p className="font-semibold text-blue-900">Assignee & Reviewer (optional)</p>
                                    <ul className="space-y-1">
                                        <li><strong>assignee:</strong> Sales person the customer is assigned to. Enter either their Employee ID or email.</li>
                                        <li><strong>review:</strong> Free-text review to attach to the customer.</li>
                                        <li><strong>reviewer:</strong> Person who gives the review (Employee ID or email). If left blank, the <strong>assignee</strong> is used as the reviewer.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: PREVIEW */}
                    {step === 'preview' && (
                        <div className="space-y-6">
                            {isValidating ? (
                                <div className="flex flex-col items-center justify-center p-12 space-y-4">
                                    <Loader2 className="h-10 w-10 animate-spin text-green-600" />
                                    <p className="text-gray-600 font-medium">Analyzing rows and checking for duplicates...</p>
                                </div>
                            ) : previewData && (
                                <>
                                    <div className="grid grid-cols-4 gap-3">
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-center">
                                            <p className="text-2xl font-bold text-gray-700">{parsedCustomers.length}</p>
                                            <p className="text-[10px] uppercase font-bold text-gray-400">Total Rows</p>
                                        </div>
                                        <div className={`rounded-lg p-3 border text-center ${errorCount > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                                            <p className={`text-2xl font-bold ${errorCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{errorCount}</p>
                                            <p className={`text-[10px] uppercase font-bold ${errorCount > 0 ? 'text-red-400' : 'text-gray-400'}`}>Errors</p>
                                        </div>
                                        <div className="bg-blue-50 border-blue-100 rounded-lg p-3 border text-center">
                                            <p className="text-2xl font-bold text-blue-700">{createCount}</p>
                                            <p className="text-[10px] uppercase font-bold text-blue-400">To Create</p>
                                        </div>
                                        <div className="bg-amber-50 border-amber-100 rounded-lg p-3 border text-center">
                                            <p className="text-2xl font-bold text-amber-700">{updateCount}</p>
                                            <p className="text-[10px] uppercase font-bold text-amber-400">To Update</p>
                                        </div>
                                    </div>

                                    {errorCount > 0 && (
                                        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-semibold text-red-800 flex items-center">
                                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                                    Validation Failed for {errorCount} rows
                                                </h4>
                                                <button
                                                    onClick={() => {
                                                        const txt = previewData.invalid.map(r => `Row ${r.row}: ${r.errors.join('; ')}`).join('\n');
                                                        navigator.clipboard.writeText(txt);
                                                        toast.success('Errors copied to clipboard');
                                                    }}
                                                    className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-1 rounded-md flex items-center gap-1 hover:bg-red-200"
                                                >
                                                    <Copy className="h-3 w-3" /> Copy Logs
                                                </button>
                                            </div>
                                            <div className="max-h-32 overflow-y-auto space-y-1 pr-2">
                                                {previewData.invalid.map((row, i) => (
                                                    <div key={i} className="text-[11px] text-red-700 font-medium">
                                                        <span className="font-bold">Row {row.row}:</span> {row.errors.join(', ')}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-xs">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Status</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Action</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Company</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Contact</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Email/Phone</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Location</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Assignee</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Reviewer</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {[...previewData.valid, ...previewData.invalid].slice(0, 10).map((row, i) => {
                                                        const isRowValid = !previewData.invalid.find(inv => inv.row === row.row);
                                                        return (
                                                            <tr key={i} className={isRowValid ? 'hover:bg-gray-50' : 'bg-red-50/30'}>
                                                                <td className="px-4 py-3">
                                                                    {isRowValid ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${row.data._action === 'update' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                        {row.data._action === 'update' ? 'Update' : 'Create'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 font-medium text-gray-900 truncate max-w-[150px]">
                                                                    <div>{row.data.companyName || row.data.customerId}</div>
                                                                    {row.data.customerId && (
                                                                        <div className="text-[10px] text-gray-400 truncate">{row.data.customerId}</div>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3 text-gray-600">{row.data.contactPerson}</td>
                                                                <td className="px-4 py-3 text-gray-600">
                                                                    <div className="flex flex-col">
                                                                        <span>{row.data.email || 'N/A'}</span>
                                                                        <span className="text-gray-400">{row.data.phone || 'N/A'}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-gray-600">{row.data.billingAddress_city || 'N/A'}</td>
                                                                <td className="px-4 py-3 text-gray-600 truncate max-w-[140px]" title={row.data._assigneeName || ''}>
                                                                    {row.data._assigneeName || <span className="text-gray-300">—</span>}
                                                                </td>
                                                                <td className="px-4 py-3 text-gray-600 truncate max-w-[140px]" title={row.data._reviewerName || ''}>
                                                                    {row.data._hasReview
                                                                        ? (row.data._reviewerName || <span className="text-gray-300">—</span>)
                                                                        : <span className="text-gray-300">—</span>}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        {parsedCustomers.length > 10 && (
                                            <div className="bg-gray-50 px-4 py-2 text-[10px] text-gray-400 text-right italic font-medium">
                                                (+ {parsedCustomers.length - 10} more rows hidden)
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* STEP 3: UPLOADING */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-16 px-8">
                            <Loader2 className="h-14 w-14 animate-spin text-green-600 mb-6" />
                            <h3 className="text-xl font-bold text-gray-900">Processing Customers</h3>
                            <p className="text-gray-500 mt-2 mb-8">Creating and updating records...</p>

                            <div className="w-full max-w-md">
                                <div className="flex items-center justify-between text-sm font-medium text-gray-600 mb-2">
                                    <span>{progress ? `${progress.current} of ${progress.total}` : 'Starting...'}</span>
                                    <span>{progress?.percent || 0}%</span>
                                </div>
                                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-green-600 rounded-full transition-all duration-300"
                                        style={{ width: `${progress?.percent || 0}%` }}
                                    />
                                </div>
                                <div className="flex items-center justify-center gap-6 mt-4 text-xs font-medium">
                                    <span className="text-green-600">Created: {progress?.created || 0}</span>
                                    <span className="text-amber-600">Updated: {progress?.updated || 0}</span>
                                    <span className="text-red-500">Failed: {progress?.failed || 0}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 4: RESULT */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`rounded-2xl p-8 text-center ${uploadResult.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                                {uploadResult.success ? (
                                    <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
                                ) : (
                                    <XCircle className="h-20 w-20 text-red-500 mx-auto mb-4" />
                                )}
                                <h3 className={`text-2xl font-bold ${uploadResult.success ? 'text-green-900' : 'text-red-900'}`}>
                                    {uploadResult.success ? 'Bulk Upload Complete!' : 'Upload Encountered Errors'}
                                </h3>
                                <p className={`mt-2 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                    {uploadResult.error || (uploadResult.success ? 'Your customers have been processed successfully.' : 'Check the details below.')}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
                                    <p className="text-3xl font-bold text-green-600">{uploadResult.data?.created || 0}</p>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mt-1">Created</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
                                    <p className="text-3xl font-bold text-amber-600">{uploadResult.data?.updated || 0}</p>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mt-1">Updated</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
                                    <p className="text-3xl font-bold text-red-400">{uploadResult.data?.failed || 0}</p>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mt-1">Failed</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
                                    <p className="text-3xl font-bold text-blue-600">{uploadResult.data?.reviewsCreated || 0}</p>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mt-1">Reviews</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <div>
                        {step === 'preview' && (
                            <Button variant="outline" onClick={() => setStep('upload')}>Back to Upload</Button>
                        )}
                        {step !== 'uploading' && step !== 'result' && (
                            <Button variant="outline" onClick={handleClose}>Cancel</Button>
                        )}
                    </div>
                    <div>
                        {step === 'preview' && !isValidating && (
                            <Button
                                variant="primary"
                                className="bg-green-600 hover:bg-green-700 px-8 text-white"
                                disabled={validCount === 0 || isUploading}
                                onClick={handleUpload}
                                icon={<Upload className="h-4 w-4" />}
                            >
                                {isUploading ? 'Uploading...' : `Process ${validCount} Rows`}
                            </Button>
                        )}
                        {step === 'result' && (
                            <Button
                                variant="primary"
                                className="bg-green-600 hover:bg-green-700 px-10 text-white"
                                onClick={() => { if (uploadResult.success) onComplete(); handleClose(); }}
                            >
                                Done
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkCustomerUpload;
